import { isLocale, Locale } from '../i18n';

export type LocaleSyncAction =
  | { type: 'apply'; locale: Locale }
  | { type: 'persist'; locale: Locale };

export const SIGN_IN_LOCALE_STORAGE_KEY = 'locale-from-sign-in';

const DEFAULT_ACCOUNT_LOCALE: Locale = 'en';

export function markSignInLocale(locale: Locale) {
  try {
    localStorage.setItem(SIGN_IN_LOCALE_STORAGE_KEY, locale);
    localStorage.setItem('locale', locale);
  } catch {
    // ignore storage errors
  }
}

export function peekSignInLocale(): Locale | null {
  try {
    const value = localStorage.getItem(SIGN_IN_LOCALE_STORAGE_KEY);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

export function clearSignInLocale() {
  try {
    localStorage.removeItem(SIGN_IN_LOCALE_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
}

export function localeRedirectUrl(origin: string, locale: Locale): string {
  const url = new URL(origin);
  url.searchParams.set('lang', locale);
  return url.toString();
}

export function takeLocaleFromSearch(search: string): Locale | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const value = params.get('lang');
  return isLocale(value) ? value : null;
}

export function getLocaleSyncAction(
  currentLocale: Locale,
  savedLocale: string | null | undefined,
  onboardingComplete: boolean,
  signInLocale: Locale | null = null,
): LocaleSyncAction | null {
  if (signInLocale) {
    if (signInLocale === savedLocale) {
      return currentLocale === signInLocale ? null : { type: 'apply', locale: signInLocale };
    }
    return { type: 'persist', locale: signInLocale };
  }

  const saved = isLocale(savedLocale) ? savedLocale : null;

  if (currentLocale === saved) {
    return null;
  }

  const savedIsExplicit = Boolean(saved && saved !== DEFAULT_ACCOUNT_LOCALE);

  // Returning users with an explicit (non-default) preference keep it when this
  // device is still on default English. Default English in preferences must not
  // overwrite a language just chosen on sign-in.
  if (onboardingComplete && savedIsExplicit && currentLocale === DEFAULT_ACCOUNT_LOCALE) {
    return { type: 'apply', locale: saved as Locale };
  }

  return { type: 'persist', locale: currentLocale };
}
